// Mobile-first shell for the technician app (/tech) — Concept DESIGN STANDARD (2026-09-04).
// v2 (2026-09-04): multi-role. The header shows the role the user is working in (tap to switch); the mail button lists
// every shared mailbox that role may open (tech@, inbox@, ...); the bottom bar adapts to the role; and "More" lists
// every function the user is authorized for (tickets, schedule, inventory, Tech Inbox, team, admin, ...) — those pages
// render inside this shell at /tech/<page>, so a dispatcher or admin on a phone never has to leave the app.
// Rendered OUTSIDE MainLayout so phones get a clean, single-column, thumb-friendly UI.
import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar, Avatar, Badge, BottomNavigation, BottomNavigationAction, Box, Divider, IconButton, ListItemIcon, ListItemText, ListSubheader, Menu, MenuItem,
  Paper, Toolbar, Typography,
} from '@mui/material';
import {
  ArrowBack as BackIcon, DesktopWindows as DesktopIcon, LocalShipping as TruckIcon, Logout as LogoutIcon, MoreVert as MoreIcon,
  Refresh as RefreshIcon, Mail as TechMailIcon, Today as MyDayIcon, Map as MapIcon, Menu as MenuIcon, Dashboard as DashboardIcon,
  ConfirmationNumber as TicketsIcon, Schedule as ScheduleIcon, CalendarMonth as CalendarIcon, Inventory2 as InventoryIcon,
  MoveToInbox as InboxIcon, People as TeamIcon, Devices as DevicesIcon, AccessTime as TimeClockIcon, Gavel as ComplianceIcon,
  AdminPanelSettings as AdminIcon, SwapHoriz as SwitchIcon, MarkEmailUnread as MailboxIcon, SupportAgent as RemoteIcon,
} from '@mui/icons-material';
import { useAuth } from '@/contexts/AuthContext';
import { useWorkspace, WsMailbox } from '@/contexts/WorkspaceContext';
import api, { techMailApi } from '@/services/api';
import { CC, GRAD } from '@/theme/theme';
import { TransferRequestDialog } from './TechShared';
import RolePickerDialog, { RoleChip } from './RolePicker';

const MARK = 'https://concept.bz/ui/logo/concept-mark.svg';

/** opens a shared mailbox in a new tab, already signed in (5-minute signed link from the API). No mailbox = the active role's first. */
export function openTechMail(mailbox?: string) {
  const w = window.open('', '_blank');
  techMailApi.link(mailbox).then((r: any) => { if (w) w.location.href = r.data.url; else window.open(r.data.url, '_blank'); })
    .catch(() => { if (w) w.close(); });
}

const ICONS: Record<string, JSX.Element> = {
  today: <MyDayIcon />, dashboard: <DashboardIcon />, tickets: <TicketsIcon />, schedule: <ScheduleIcon />, calendar: <CalendarIcon />,
  map: <MapIcon />, inventory: <InventoryIcon />, inbox: <InboxIcon />, team: <TeamIcon />, devices: <DevicesIcon />,
  timeclock: <TimeClockIcon />, compliance: <ComplianceIcon />, admin: <AdminIcon />, remote: <RemoteIcon />,
};
const iconFor = (key: string) => ICONS[key] || <DashboardIcon />;

// Pages that were built for the desktop need the full width; My Day / ticket detail keep the narrow phone column.
const isHome = (p: string) => p === '/tech' || p === '/tech/my-day';
const NARROW = (p: string) => isHome(p) || p.startsWith('/tech/ticket/');
const TITLES: Record<string, string> = {
  '/tech': 'My Day', '/tech/my-day': 'My Day', '/tech/dashboard': 'Dashboard', '/tech/tickets': 'Tickets', '/tech/schedule': 'Schedule', '/tech/calendar': 'Calendar',
  '/tech/map': 'Map', '/tech/inventory': 'Inventory', '/tech/inbox': 'Tech Inbox', '/tech/team': 'Team', '/tech/devices': 'Devices',
  '/tech/compliance': 'Compliance', '/tech/admin': 'Admin',
};

export default function TechLayout() {
  const { user, logout } = useAuth();
  const { ws, activeRoleName, reload } = useWorkspace();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [mailAnchor, setMailAnchor] = useState<null | HTMLElement>(null);
  const [transferOpen, setTransferOpen] = useState(false);
  const [roleOpen, setRoleOpen] = useState(false);
  const [accounts, setAccounts] = useState<WsMailbox[]>([]);

  const path = location.pathname;
  // Embedded in the Concept Staff native app (app/web.tsx): the phone supplies header + navigation, so hide ours.
  const embedded = (() => { try { const q = new URLSearchParams(location.search); if (q.get('embedded') === '1') sessionStorage.setItem('concept_embedded', '1'); return sessionStorage.getItem('concept_embedded') === '1'; } catch { return false; } })();
  const onDetail = path.startsWith('/tech/ticket/');
  const title = onDetail ? 'Ticket' : (TITLES[path] || 'Dispatch');
  const initials = `${(user as any)?.first_name?.[0] || ''}${(user as any)?.last_name?.[0] || ''}` || (user?.email?.[0] || 'T').toUpperCase();
  const fireRefresh = () => window.dispatchEvent(new CustomEvent('tech-refresh'));

  // mailboxes the user may open (all roles) with unseen counts — refreshed every 60 s
  useEffect(() => {
    let live = true;
    const tick = () => api.get('/tech-mail/accounts').then((r: any) => live && setAccounts(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    tick(); const h = setInterval(tick, 60000);
    return () => { live = false; clearInterval(h); };
  }, [ws?.active_role?.id]);

  // first sign-in with several roles: ask which one they are working in
  useEffect(() => { if (ws?.needs_role_choice) setRoleOpen(true); }, [ws?.needs_role_choice]);

  // active-role mailboxes first, then the rest the user's other roles allow
  const roleBoxes = new Set((ws?.mailboxes || []).map((m) => m.address));
  const primary = accounts.filter((a) => roleBoxes.has(a.address));
  const others = accounts.filter((a) => !roleBoxes.has(a.address));
  const unseenTotal = primary.reduce((n, a) => n + (a.unseen || 0), 0);
  const mailClick = (e: React.MouseEvent<HTMLElement>) => {
    if (accounts.length <= 1) openTechMail(accounts[0]?.address); else setMailAnchor(e.currentTarget);
  };

  // bottom bar: from the active role's default, mapped onto authorized functions
  const functions = ws?.functions || [];
  const fnByKey = (k: string) => functions.find((f) => f.key === k);
  const navKeys = (ws?.nav && ws.nav.length ? ws.nav : ['my_day', 'map', 'mail', 'desktop', 'more'])
    .filter((k) => ['mail', 'desktop', 'more'].includes(k) || fnByKey(k));
  const navItems = navKeys.map((k) => {
    if (k === 'mail') return { key: k, label: 'Mail', icon: unseenTotal ? <Badge badgeContent={unseenTotal} color="secondary" sx={{ '& .MuiBadge-badge': { fontSize: 9, minWidth: 15, height: 15 } }}><TechMailIcon /></Badge> : <TechMailIcon />, path: '' };
    if (k === 'desktop') return { key: k, label: 'Desktop', icon: <DesktopIcon />, path: '/dashboard' };
    if (k === 'more') return { key: k, label: 'More', icon: <MenuIcon />, path: '' };
    const f = fnByKey(k)!; return { key: k, label: f.label, icon: iconFor(f.icon), path: f.path };
  });
  const navValue = navItems.findIndex((n) => n.path && (n.path === path || (n.path === '/tech/my-day' && isHome(path)) || (n.path !== '/tech/my-day' && n.path !== '/dashboard' && path.startsWith(n.path.split('?')[0]))));
  const navChange = (i: number, e?: any) => {
    const n = navItems[i]; if (!n) return;
    if (n.key === 'mail') mailClick(e); else if (n.key === 'more') setMenuAnchor(e.currentTarget); else if (/^https?:/.test(n.path)) window.open(n.path, '_blank', 'noopener'); else navigate(n.path);
  };

  const MailboxItem = ({ a }: { a: WsMailbox }) => (
    <MenuItem onClick={() => { setMailAnchor(null); setMenuAnchor(null); openTechMail(a.address); }}>
      <ListItemIcon>{a.unseen ? <Badge badgeContent={a.unseen} color="secondary" sx={{ '& .MuiBadge-badge': { fontSize: 9, minWidth: 15, height: 15 } }}><MailboxIcon fontSize="small" /></Badge> : <TechMailIcon fontSize="small" />}</ListItemIcon>
      <ListItemText primary={a.label} secondary={a.address} />
    </MenuItem>
  );

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: CC.bg, display: 'flex', flexDirection: 'column' }}>
      {!embedded && <AppBar position="sticky" elevation={0} sx={{ background: CC.navy }}>
        <Toolbar sx={{ minHeight: 56, gap: 0.5 }}>
          {onDetail || !isHome(path) ? (
            <IconButton edge="start" color="inherit" onClick={() => navigate(onDetail ? '/tech' : (fnByKey('my_day') ? '/tech' : (navItems[0]?.path || '/tech')))} sx={{ mr: 0.5 }} aria-label="Back"><BackIcon /></IconButton>
          ) : (
            <Box component="img" src={MARK} alt="" sx={{ width: 30, height: 30, mr: 1, filter: 'drop-shadow(1px 1px 0 rgba(0,0,0,.45))' }} />
          )}
          <Typography sx={{ font: `600 16px ${CC.fontBody}`, flexGrow: 1, minWidth: 0 }} noWrap>{title}</Typography>
          {(activeRoleName === 'Technician' || !activeRoleName) && (
            <IconButton color="inherit" onClick={() => setTransferOpen(true)} aria-label="Request part to my truck"><TruckIcon /></IconButton>
          )}
          <IconButton color="inherit" onClick={fireRefresh} aria-label="Refresh"><RefreshIcon /></IconButton>
          <IconButton color="inherit" onClick={mailClick} aria-label="Mail" sx={{ background: 'rgba(255,255,255,.10)', border: '1px solid rgba(255,255,255,.16)', borderRadius: 1.5, width: 40, height: 36 }}>
            <Badge badgeContent={unseenTotal} color="secondary" invisible={!unseenTotal} sx={{ '& .MuiBadge-badge': { boxShadow: `0 0 0 2px ${CC.navy}`, fontSize: 10, height: 16, minWidth: 16 } }}><TechMailIcon /></Badge>
          </IconButton>
          <IconButton color="inherit" onClick={(e) => setMenuAnchor(e.currentTarget)} aria-label="Menu"><MoreIcon /></IconButton>
        </Toolbar>
        {(ws?.roles.length || 0) > 1 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, pb: 0.75, mt: -0.25 }}>
            <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,.75)' }}>Working as</Typography>
            <RoleChip />
            <Typography onClick={() => setRoleOpen(true)} sx={{ fontSize: 12, color: 'rgba(255,255,255,.75)', textDecoration: 'underline', cursor: 'pointer', ml: 'auto' }}>switch</Typography>
          </Box>
        )}
      </AppBar>}

      {/* mailbox chooser (only when the user's roles give more than one) */}
      <Menu anchorEl={mailAnchor} open={!!mailAnchor} onClose={() => setMailAnchor(null)} PaperProps={{ sx: { minWidth: 260 } }}>
        {primary.length > 0 && <ListSubheader sx={{ lineHeight: '28px', fontWeight: 700, color: CC.navy }}>{activeRoleName || 'Your'} mailboxes</ListSubheader>}
        {primary.map((a) => <MailboxItem key={a.address} a={a} />)}
        {others.length > 0 && <ListSubheader sx={{ lineHeight: '28px', fontWeight: 700, color: CC.navy }}>Other roles</ListSubheader>}
        {others.map((a) => <MailboxItem key={a.address} a={a} />)}
        {accounts.length === 0 && <MenuItem disabled>No shared mailbox for your roles</MenuItem>}
      </Menu>

      {/* More: identity, role switch, every authorized function, mailboxes, desktop, log out */}
      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)} PaperProps={{ sx: { minWidth: 270, maxHeight: '80vh' } }}>
        <MenuItem disabled sx={{ opacity: '1 !important' }}>
          <ListItemIcon><Avatar sx={{ width: 28, height: 28, fontSize: '0.8rem', background: GRAD.accent }}>{initials}</Avatar></ListItemIcon>
          <ListItemText primary={`${(user as any)?.first_name || ''} ${(user as any)?.last_name || ''}`.trim() || user?.email} secondary={activeRoleName ? `Working as ${activeRoleName}` : user?.email} />
        </MenuItem>
        {(ws?.roles.length || 0) > 1 && (
          <MenuItem onClick={() => { setMenuAnchor(null); setRoleOpen(true); }}>
            <ListItemIcon><SwitchIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Switch role" secondary={(ws?.roles || []).map((r) => r.name).join(' · ')} />
          </MenuItem>
        )}
        <Divider />
        <ListSubheader sx={{ lineHeight: '28px', fontWeight: 700, color: CC.navy }}>Functions</ListSubheader>
        {functions.map((f) => (
          <MenuItem key={f.key} selected={f.path === path} onClick={() => { setMenuAnchor(null); if (/^https?:/.test(f.path)) window.open(f.path, '_blank', 'noopener'); else navigate(f.path); }}>
            <ListItemIcon sx={{ color: f.path === path ? CC.red : 'inherit' }}>{iconFor(f.icon)}</ListItemIcon>
            <ListItemText primary={f.label} />
          </MenuItem>
        ))}
        <Divider />
        <ListSubheader sx={{ lineHeight: '28px', fontWeight: 700, color: CC.navy }}>Mail</ListSubheader>
        {accounts.map((a) => <MailboxItem key={a.address} a={a} />)}
        {accounts.length === 0 && <MenuItem disabled>No shared mailbox for your roles</MenuItem>}
        <Divider />
        <MenuItem onClick={() => { setMenuAnchor(null); navigate('/dashboard'); }}>
          <ListItemIcon><DesktopIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Desktop site" />
        </MenuItem>
        <MenuItem onClick={async () => { setMenuAnchor(null); await logout(); navigate('/login'); }}>
          <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Log out" />
        </MenuItem>
      </Menu>

      <Box component="main" sx={{ flex: 1, width: '100%', maxWidth: NARROW(path) ? 520 : '100%', mx: 'auto', px: NARROW(path) ? 1.5 : 1, pt: NARROW(path) ? 1.5 : 1, pb: embedded ? 2 : 11, minWidth: 0 }}>
        <Outlet />
      </Box>

      {!embedded && <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1200, borderRadius: 0, background: CC.navy, pb: 'env(safe-area-inset-bottom)' }} elevation={3}>
        <BottomNavigation value={navValue} showLabels sx={{ height: 64, background: CC.navy, px: 0.5 }} onChange={(e, v) => navChange(v, e)}>
          {navItems.map((n) => <BottomNavigationAction key={n.key} label={n.label} icon={n.icon} />)}
        </BottomNavigation>
      </Paper>}

      <TransferRequestDialog open={transferOpen} onClose={() => setTransferOpen(false)} onDone={fireRefresh} />
      <RolePickerDialog open={roleOpen} forced={!!ws?.needs_role_choice} onClose={() => setRoleOpen(false)}
        onChosen={(name) => { reload(); navigate(name === 'Technician' ? '/tech/my-day' : '/tech'); }} />
    </Box>
  );
}
